import React, { useCallback, useEffect, useState } from 'react';
import './Styles.css';
import CopyCostCentersButtons from './Buttons/CopyCostCentersButtons';
import { formatMessage } from '../../../../../Localization/formatMessage';
import Messages from '../../../../../Localization/Messages';
import CheckBox from '../../../../../Elements/ids/InputElements/CheckBox';
import {
  mapAgentsToCostCenters,
  generateCreateRequest,
  checkAllCostCenters,
  updateDataInRow,
  validateCodePrefixInput,
  validateFieldInputs,
  canFormBeSubmitted,
} from './Functions';
import TextInput from '../../../../../Elements/ids/InputElements/TextInput';
import Form from '../../../../../Elements/Form';
import { createServerErrorMessage } from '../../../../../Helpers/FormInputHelper';
import ErrorMessage from '../../../../../Elements/ErrorMessage';
import { ServiceClient } from '../../../../../Services/ServiceClient';
import { PresentationMode } from '../../../../../Enums/PresentationMode';
import { useDebouncedEffect } from '../../../../../Helpers/CustomHooks';
import Modal from '../../../../../Elements/ids/Modal';

export const copyCostCentersFormId = 'copyCostCenters';

const CopyCostCenters = ({ closeView, team }) => {
  const [canSubmit, setCanSubmit] = useState(false);
  const [errors, setErrors] = useState({
    codePrefix: '',
    name: '',
    costCenters: null,
    [copyCostCentersFormId]: null,
  });
  const [newTeamData, setNewTeamData] = useState({
    isCreated: false,
    team: {},
  });
  const [modalMode, setModalMode] = useState(PresentationMode.View);
  const [formData, setFormData] = useState({
    costCenters: mapAgentsToCostCenters(team.agents),
    allCostCentersChecked: true,
    codePrefix: '',
  });

  const getCommonFields = (propertyName, isMandatory) => {
    const handleChange = e => {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });
    };

    return {
      name: propertyName,
      key: propertyName,
      onChange: handleChange,
      value: formData[propertyName] ?? '',
      error: errors[propertyName],
      isMandatory: isMandatory,
    };
  };

  const onSubmit = event => {
    event.preventDefault();

    ServiceClient.createTeam(
      formData.codePrefix,
      generateCreateRequest(formData),
      data => {
        setNewTeamData({ isCreated: true, team: data });
      },
      error => {
        setErrors([createServerErrorMessage(error, copyCostCentersFormId)]);
        setModalMode(PresentationMode.Error);
      }
    );
  };

  // Code prefix is validated separately to not call API when code prefix field was not changed
  const validateCodePrefix = useCallback(() => {
    validateCodePrefixInput(formData.codePrefix).then(result => {
      setErrors({ ...errors, codePrefix: result.error });
      if (result.nameFromAd) {
        setFormData({ ...formData, name: result.nameFromAd });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.codePrefix]);

  const validateFormData = useCallback(() => {
    const result = validateFieldInputs(formData);
    setErrors({ ...errors, ...result });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useDebouncedEffect(validateFormData, 500);
  useDebouncedEffect(validateCodePrefix, 500);

  useEffect(() => {
    setCanSubmit(canFormBeSubmitted(errors));
  }, [errors]);

  return (
    <Modal
      closeView={closeView}
      titleText={formatMessage(Messages.copyCostCenters_title)}
      buttons={
        <CopyCostCentersButtons
          closeView={closeView}
          canSubmit={canSubmit}
          modalMode={modalMode}
          closeError={() => {
            setModalMode(PresentationMode.View);
            setErrors([]);
          }}
          newTeamData={newTeamData}
          newTeamCodePrefix={formData.codePrefix}
        />
      }>
      <div className="teamPage-costCenters-container">
        <Form
          formId={copyCostCentersFormId}
          onSubmit={e => {
            onSubmit(e);
          }}
          disabled={modalMode === PresentationMode.Error || newTeamData.isCreated}>
          <div className="common-table-row teamPage-costCenters-tableRow headerRow">
            <CheckBox
              checked={formData.allCostCentersChecked}
              checkBoxId={'allCostCenters'}
              onChange={() => {
                checkAllCostCenters(formData, setFormData);
              }}
              label={formatMessage(Messages.copyCostCenters_chooseAll)}
            />
            <div>{formatMessage(Messages.common_costCenter)}</div>
            <div>{formatMessage(Messages.common_externalSalesCode)}</div>
          </div>
          {formData.costCenters.map((cc, index) => {
            return (
              <div
                className={`common-table-row teamPage-costCenters-tableRow ${
                  index % 2 === 1 ? 'common-modalTable-evenRow' : ''
                }`}
                key={index}>
                <CheckBox
                  checked={cc.checked}
                  checkBoxId={cc.id}
                  onChange={() => {
                    updateDataInRow(cc.id, 'checked', !cc.checked, formData, setFormData);
                  }}
                />
                <div>{cc.displayName}</div>
                <TextInput
                  {...getCommonFields('externalSalesCode')}
                  placeholder={formatMessage(Messages.common_optional)}
                  disabled={!cc.checked}
                  onChange={e => {
                    updateDataInRow(cc.id, e.target.name, e.target.value, formData, setFormData);
                  }}
                  value={formData.costCenters.find(costCenter => costCenter.id === cc.id)?.externalSalesCode ?? ''}
                  cssClasses="teamPage-costCenters-externalSalesCode"
                />
              </div>
            );
          })}

          <ErrorMessage message={errors.costCenters} />

          <TextInput
            label={formatMessage(Messages.common_codePrefix)}
            value={formData.codePrefix}
            onChange={e => {
              setFormData({ ...formData, codePrefix: e.target.value });
            }}
            error={errors['codePrefix']}
            isMandatory={true}
            cssClasses="teamPage-costCenters_code-prefix"
          />
          <TextInput
            {...getCommonFields('name', true)}
            label={formatMessage(Messages.common_name)}
            cssClasses="teamPage-costCenters-name"
          />
          <div className="teamPage-costCenters-errorContainer">
            <ErrorMessage message={errors[copyCostCentersFormId]} />
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default CopyCostCenters;
